import type { ReactNode } from 'react';

import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkDivider from '@skyscanner-internal/backpack-web/bpk-component-divider';
import {
  BpkBox,
  BpkSpacing,
  BpkStack,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkSkeleton, {
  IMAGE_SKELETON_STYLE,
  SKELETON_TYPES,
  SIZE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-skeleton';

import './RecommendationCardSkeleton.scss';

const SectionSkeleton = ({ children }: { children: ReactNode }) => (
  <BpkStack
    direction="column"
    minWidth="0rem"
    justify="space-between"
    flexGrow={1}
    gap={BpkSpacing.MD}
  >
    {children}
  </BpkStack>
);

const RowSkeleton = ({ children }: { children: ReactNode }) => (
  <BpkStack direction="row" align="center" gap={BpkSpacing.MD}>
    {children}
  </BpkStack>
);

const LinesSkeleton = ({ children }: { children: ReactNode }) => (
  <BpkStack direction="column" flexGrow={1} gap={BpkSpacing.MD}>
    {children}
  </BpkStack>
);

const CtaSkeleton = ({ children }: { children: ReactNode }) => (
  <BpkBox width="50%">{children}</BpkBox>
);

const FlightSectionSkeleton = () => (
  <SectionSkeleton>
    <BpkSkeleton type={SKELETON_TYPES.bodyText} size={SIZE_TYPES.small} />
    <RowSkeleton>
      <BpkSkeleton
        type={SKELETON_TYPES.image}
        size={{ width: '2.5rem', height: '2.5rem' }}
        style={IMAGE_SKELETON_STYLE.rounded}
      />
      <LinesSkeleton>
        <BpkSkeleton type={SKELETON_TYPES.bodyText} size={SIZE_TYPES.default} />
        <BpkSkeleton type={SKELETON_TYPES.bodyText} size={SIZE_TYPES.small} />
      </LinesSkeleton>
    </RowSkeleton>
    <RowSkeleton>
      <BpkSkeleton
        type={SKELETON_TYPES.image}
        size={{ width: '2.5rem', height: '2.5rem' }}
        style={IMAGE_SKELETON_STYLE.rounded}
      />
      <LinesSkeleton>
        <BpkSkeleton type={SKELETON_TYPES.bodyText} size={SIZE_TYPES.default} />
        <BpkSkeleton type={SKELETON_TYPES.bodyText} size={SIZE_TYPES.small} />
      </LinesSkeleton>
    </RowSkeleton>
    <CtaSkeleton>
      <BpkSkeleton type={SKELETON_TYPES.bodyText} size={SIZE_TYPES.default} />
    </CtaSkeleton>
  </SectionSkeleton>
);

const ImageSectionSkeleton = () => (
  <SectionSkeleton>
    <BpkSkeleton type={SKELETON_TYPES.bodyText} size={SIZE_TYPES.small} />
    <RowSkeleton>
      <BpkSkeleton
        type={SKELETON_TYPES.image}
        size={{ width: '4rem', height: '4rem' }}
        style={IMAGE_SKELETON_STYLE.rounded}
      />
      <LinesSkeleton>
        <BpkSkeleton type={SKELETON_TYPES.bodyText} size={SIZE_TYPES.default} />
        <BpkSkeleton type={SKELETON_TYPES.bodyText} size={SIZE_TYPES.small} />
        <BpkSkeleton type={SKELETON_TYPES.bodyText} size={SIZE_TYPES.small} />
      </LinesSkeleton>
    </RowSkeleton>
    <CtaSkeleton>
      <BpkSkeleton type={SKELETON_TYPES.bodyText} size={SIZE_TYPES.default} />
    </CtaSkeleton>
  </SectionSkeleton>
);

const RecommendationCardSkeleton = () => (
  <BpkCard
    className="recommendation-card-skeleton"
    padded={false}
    atomic={false}
    data-testid="recommendation-card-skeleton"
    aria-hidden="true"
  >
    <BpkStack
      direction="column"
      gap={BpkSpacing.Base}
      paddingBlock={BpkSpacing.MD}
      paddingInline={BpkSpacing.Base}
    >
      <BpkStack direction="row" justify="space-between">
        <BpkBox width="25%">
          <BpkSkeleton type={SKELETON_TYPES.headline} size={SIZE_TYPES.small} />
        </BpkBox>
        <BpkBox width="35%">
          <BpkSkeleton type={SKELETON_TYPES.headline} size={SIZE_TYPES.small} />
        </BpkBox>
      </BpkStack>
      <BpkDivider />
      <BpkStack
        direction={{ base: 'column', desktop: 'row' }}
        gap={BpkSpacing.LG}
      >
        <FlightSectionSkeleton />
        <ImageSectionSkeleton />
        <ImageSectionSkeleton />
      </BpkStack>
      <BpkDivider />
      <BpkStack direction="row" justify="center">
        <BpkSkeleton type={SKELETON_TYPES.bodyText} size={SIZE_TYPES.default} />
      </BpkStack>
    </BpkStack>
  </BpkCard>
);

export default RecommendationCardSkeleton;
